import { useEffect, useState } from "react";
import { api, loadUser, logout, fmt$, STATUS_COLORS, saveAuth } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { LogOut, KeyRound, Camera, Trash2 } from "lucide-react";
import { Avatar, fileToCompressedDataUri } from "@/components/site/Avatar";
import { ChangePasswordModal } from "@/pages/AdminHome";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function EmployeePortal() {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [avail, setAvail] = useState({ weekly: [], exceptions: [] });
  const [me, setMe] = useState(null);
  const [pwModal, setPwModal] = useState(false);
  const user = loadUser();
  const nav = useNavigate();

  const loadMe = () => api.get("/auth/me").then((r) => setMe(r.data));

  useEffect(() => {
    api.get("/employee/jobs").then((r) => setJobs(r.data));
    api.get("/employee/availability").then((r) => setAvail({ weekly: r.data.weekly || [], exceptions: r.data.exceptions || [] }));
    loadMe();
  }, []);

  const pickPhoto = async (file) => {
    if (!file) return;
    try {
      const data_uri = await fileToCompressedDataUri(file, { maxSize: 512, quality: 0.85 });
      if (data_uri.length > 400_000) { toast.error("Image still too large after compression"); return; }
      await api.post("/users/me/photo", { data_uri });
      toast.success("Photo updated");
      // Keep local user in sync so header refreshes
      const cached = loadUser();
      if (cached) saveAuth(localStorage.getItem("ngs_token"), { ...cached, photo_data_uri: data_uri });
      loadMe();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not upload"); }
  };

  const removePhoto = async () => {
    try {
      await api.delete("/users/me/photo");
      toast.success("Photo removed");
      const cached = loadUser();
      if (cached) saveAuth(localStorage.getItem("ngs_token"), { ...cached, photo_data_uri: null });
      loadMe();
    } catch (e) { toast.error("Error"); }
  };

  const setDay = (idx, patch) => {
    const w = [...avail.weekly];
    const existing = w.findIndex((x) => x.weekday === idx);
    if (existing >= 0) w[existing] = { ...w[existing], ...patch };
    else w.push({ weekday: idx, start: "09:00", end: "17:00", ...patch });
    setAvail({ ...avail, weekly: w });
  };
  const removeDay = (idx) => setAvail({ ...avail, weekly: avail.weekly.filter((x) => x.weekday !== idx) });
  const dayFor = (idx) => avail.weekly.find((x) => x.weekday === idx);

  const save = async () => {
    await api.put("/employee/availability", avail);
    toast.success("Availability saved");
  };

  return (
    <div className="bg-[#0A0B0E] min-h-screen text-white p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Avatar src={me?.photo_data_uri} name={user?.name} size={56} />
            <div>
              <div className="text-xs uppercase tracking-widest text-[#CCFF00]">Detailer Portal</div>
              <div className="font-display text-3xl mt-1">Hi, {user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label data-testid="emp-photo-upload" className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 hover:border-[#CCFF00] hover:text-[#CCFF00] text-xs uppercase tracking-widest">
              <Camera size={14} /> {me?.photo_data_uri ? "Change photo" : "Upload photo"}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => pickPhoto(e.target.files?.[0])} />
            </label>
            {me?.photo_data_uri && (
              <button data-testid="emp-photo-remove" onClick={removePhoto}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 hover:border-red-500 hover:text-red-300 text-xs uppercase tracking-widest">
                <Trash2 size={14} /> Remove
              </button>
            )}
            <button data-testid="emp-change-password-btn" onClick={() => setPwModal(true)}
              className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1">
              <KeyRound size={14} /> Password
            </button>
            <button data-testid="emp-logout-btn" onClick={() => { logout(); nav("/"); }}
              className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          <button data-testid="tab-jobs" onClick={() => setTab("jobs")}
            className={`px-4 py-2 rounded-xl text-sm uppercase tracking-widest ${tab === "jobs" ? "bg-[#CCFF00] text-black" : "border border-zinc-700"}`}>My Jobs</button>
          <button data-testid="tab-availability" onClick={() => setTab("avail")}
            className={`px-4 py-2 rounded-xl text-sm uppercase tracking-widest ${tab === "avail" ? "bg-[#CCFF00] text-black" : "border border-zinc-700"}`}>My Availability</button>
        </div>

        {tab === "jobs" && (
          <div className="space-y-3">
            {jobs.length === 0 && <div className="text-zinc-500">No jobs assigned yet.</div>}
            {jobs.map((j) => (
              <div key={j.id} data-testid={`emp-job-${j.id}`} className="card-surface p-5">
                <div className="flex justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-display text-xl">{j.totals?.service_name}</div>
                    <div className="text-sm text-zinc-400">{j.customer_name} · {j.vehicle_size} · {j.condition}</div>
                    <div className="text-xs text-zinc-500 mt-1">{j.confirmed_date || j.preferred_date} at {j.confirmed_time || j.preferred_time}</div>
                    <div className="text-xs text-zinc-500 mt-1">{j.address}</div>
                  </div>
                  <span className={`status-pill ${STATUS_COLORS[j.status] || ""}`}>{j.status}</span>
                </div>
                <div className="mt-2 text-xs text-zinc-400">Est. duration: {j.totals?.duration_minutes} min · {fmt$(j.totals?.total)}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "avail" && (
          <div className="card-surface p-6">
            <div className="font-display text-xl mb-4">Weekly availability</div>
            <div className="space-y-2">
              {DAYS.map((d, i) => {
                const on = !!dayFor(i);
                const cur = dayFor(i) || { start: "09:00", end: "17:00" };
                return (
                  <div key={i} className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 w-20">
                      <input type="checkbox" data-testid={`day-${d.toLowerCase()}`}
                        checked={on}
                        onChange={(e) => e.target.checked ? setDay(i, {}) : removeDay(i)} />
                      <span className="text-sm">{d}</span>
                    </label>
                    <input type="time" value={cur.start} disabled={!on}
                      onChange={(e) => setDay(i, { start: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700 disabled:opacity-40" />
                    <input type="time" value={cur.end} disabled={!on}
                      onChange={(e) => setDay(i, { end: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-zinc-700 disabled:opacity-40" />
                  </div>
                );
              })}
            </div>
            <button data-testid="save-availability-btn" onClick={save}
              className="neon-btn mt-6 px-5 py-3 rounded-xl">Save availability</button>
          </div>
        )}
      </div>
      {pwModal && <ChangePasswordModal onClose={() => setPwModal(false)} />}
    </div>
  );
}
